import { GTM_USERFLOW, STEP_NUMBERS } from "@/app/context/ProjectProvider/constants";
import { Steps } from "@/components/ProgressiveStepBar/types";
import {
  ASSET_GENERATION_TYPES,
  GA_ERROR_TYPE,
  GTM_EVENT_PAGE_NAMES,
  PROJECT_TYPES,
  STAGE_NAMES,
  VALID_GTM_EVENT_NAMES,
} from "@/constants/constants";
import { ASSET_CREATING_TITLES } from "../AddInputs/constants";
import { FormValues } from "./schema";
import { AssetTypeField } from "./enum";
import { handleEventTriggerWithPageName, handleEventTriggerWithParams } from "@/lib/analytics.helper";
import { ProjectStepsType } from "@/app/context/ProjectProvider";
import { RadioOption } from "@/components/CustomRadio";
import { RadioOptionType } from "@/types";
import { ASSETS_FIELD_LABEL, CONTINUE, DEFAULT_ASPECT_RATIO, defaultRadioOption, PROJECT_OPTIONS_CONSTANT } from "./constant";
import { ConstructResponseParams, ProjectOptionPostRequestBody, RequestBodyParams, responseObjectType } from "./types";
import { FieldErrors } from "react-hook-form";
import { FULLY_GENERATED_IMAGE, IMAGE_GENERATED_BACKGROUND } from "@/components/ProductPlacement/constants";
import { steps } from "../../constant";
interface FieldsDataConfig {
  name: string;
  label: string;
  options: RadioOptionType[];
  classNames?: string;
  wrapperClassName?: string;
  radioGroupClassName?: string;
  errorMessageId?: string;
  useFieldset?: boolean;
}

export const getImageDimensionRatio = (imageDimension: string) => {
  return imageDimension?.split("(")[1]?.split(")")[0]?.replaceAll(" ", "");
};

export const getScriptLengthValue = (scriptLength?: string) => {
  return scriptLength && Number(scriptLength.split(":")[1]);
};

export const updateAssetGenerationSteps = (updatedSteps: { assetGeneration: Steps[] }, assetType: string) => {
  let updatedAssetGenerationSteps: Steps[];

  switch (assetType) {
    case ASSET_CREATING_TITLES.BRIEF:
      updatedAssetGenerationSteps = [
        ...updatedSteps[PROJECT_TYPES.GENERATE_ASSETS].filter((step) => step.label !== "Review assets"),
        { step: STEP_NUMBERS.FOUR, label: "Confirm details" },
        { step: STEP_NUMBERS.FIVE, label: "Review assets" },
      ];
      break;

    case ASSET_CREATING_TITLES.CONCEPT:
      updatedAssetGenerationSteps = updatedSteps[PROJECT_TYPES.GENERATE_ASSETS]
        .map((step) => (step.step === STEP_NUMBERS.THREE ? { ...step, label: "Review assets" } : step))
        .slice(0, -1);
      break;

    default:
      updatedAssetGenerationSteps = updatedSteps[PROJECT_TYPES.GENERATE_ASSETS];
  }

  updatedSteps[PROJECT_TYPES.GENERATE_ASSETS] = updatedAssetGenerationSteps;

  return updatedSteps;
};

export const handleGAEventTrigger = (data: FormValues) => {
  const assetTypeEventMap = {
    [AssetTypeField.TEXT]: VALID_GTM_EVENT_NAMES.TEXT,
    [AssetTypeField.VISUAL]: VALID_GTM_EVENT_NAMES.VISUAL,
    [AssetTypeField.VISUAL_AND_TEXT]: VALID_GTM_EVENT_NAMES.VISUAL_TEXT,
    [AssetTypeField.VIDEO_SCRIPT]: VALID_GTM_EVENT_NAMES.VIDEO_SCRIPT,
  };

  const assetType = data.assetType as AssetTypeField;
  const pageName = GTM_EVENT_PAGE_NAMES.PROJECT_OPTIONS;

  if (assetTypeEventMap[assetType]) {
    handleEventTriggerWithPageName(assetTypeEventMap[assetType], pageName);
  }

  const handleEventIfLabelExists = (label: string | undefined, eventName: string, paramName: string) => {
    if (label) {
      handleEventTriggerWithParams(eventName, {
        pageName,
        [paramName]: label,
      });
    }
  };

  // Trigger events for other data fields if labels exist
  handleEventIfLabelExists(data?.channel?.label, VALID_GTM_EVENT_NAMES.CHANNEL_SELECTION, ASSETS_FIELD_LABEL.CHANNEL_NAME);
  handleEventIfLabelExists(data?.copyOutput?.label, VALID_GTM_EVENT_NAMES.OUTPUT_SELECTION, ASSETS_FIELD_LABEL.OUTPUT_NAME);
  handleEventIfLabelExists(
    data?.specifications?.label,
    VALID_GTM_EVENT_NAMES.SPECIFICATION_SELECTION,
    ASSETS_FIELD_LABEL.SPECIFICATION_SELECTION,
  );
  handleEventIfLabelExists(data?.imageDimensions, VALID_GTM_EVENT_NAMES.DIMENSION_SELECTION, ASSETS_FIELD_LABEL.DIMENSION_SELECTION);
  handleEventIfLabelExists(
    data?.scriptLength?.label,
    VALID_GTM_EVENT_NAMES.VIDEO_LENGTH_SELECTION,
    ASSETS_FIELD_LABEL.VIDEO_LENGTH_SELECTION,
  );
};

export const getUpdatedStepsByStage = (
  stage: string,
  steps: ProjectStepsType,
  initialSteps: Steps[],
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>,
) => {
  const updatedSteps = { ...steps, [PROJECT_TYPES.GENERATE_ASSETS]: initialSteps };

  switch (stage) {
    case STAGE_NAMES.ENTER_DESCRIPTION:
      setCurrentStep(STEP_NUMBERS.FOUR);
      break;

    case STAGE_NAMES.CONCEPT_SELECTION:
      updateAssetGenerationSteps(updatedSteps, ASSET_CREATING_TITLES.BRIEF);
      setCurrentStep(STEP_NUMBERS.FIVE);
      break;

    case STAGE_NAMES.USE_A_CONCEPT:
      updateAssetGenerationSteps(updatedSteps, ASSET_CREATING_TITLES.CONCEPT);
      setCurrentStep(STEP_NUMBERS.THREE);
      break;

    default:
      return steps;
  }

  return updatedSteps;
};

const determineAspectRatio = (
  data: FormValues,
  localProductPlacementImageUrl: string | undefined,
  IMAGE_DIMENSION_OPTIONS: RadioOption[],
  enableMergedPpfgFlow: boolean,
): string => {
  if (enableMergedPpfgFlow && localProductPlacementImageUrl) {
    return getImageDimensionRatio(data?.imageDimensions ?? IMAGE_DIMENSION_OPTIONS[0].id);
  }
  return getImageDimensionRatio(localProductPlacementImageUrl ? IMAGE_DIMENSION_OPTIONS[0].id : (data?.imageDimensions as string));
};

const mapAssetType = (assetType: string) => {
  if (assetType === AssetTypeField.TEXT) return ASSET_GENERATION_TYPES.TEXT;
  if (assetType === AssetTypeField.VISUAL) return ASSET_GENERATION_TYPES.VISUAL;
  if (assetType === AssetTypeField.VISUAL_AND_TEXT) return ASSET_GENERATION_TYPES.VISUAL_TEXT;
  if (assetType === AssetTypeField.VIDEO_SCRIPT) return ASSET_GENERATION_TYPES.VIDEO_SCRIPT;
};

const constructResponseObject = ({
  assetType,
  data,
  projectId,
  aspectRatio,
  localProductPlacementImageUrl,
  productPlacementImagePPType,
  productPlacementImageDocId,
  isImageIncluded,
  referenceImage,
  raiChecks,
  inputImagePath,
}: ConstructResponseParams) => {
  const updateAssetType = mapAssetType(assetType);
  const baseObject = { assetType: updateAssetType, referenceImage, raiChecks, inputImagePath };
  const conditionalProps: responseObjectType = {};

  switch (updateAssetType) {
    case "text":
      conditionalProps.text = projectId ? data?.copyOutput?.label : { text: data?.copyOutput?.label };
      break;
    case "visual":
      conditionalProps.productPlacementImageUrl = localProductPlacementImageUrl;
      conditionalProps.productPlacementImagePPType = productPlacementImagePPType;
      conditionalProps.productPlacementImageDocId = productPlacementImageDocId;
      if (projectId) conditionalProps.aspectRatio = aspectRatio;
      conditionalProps["visual"] = {
        aspectRatio,
      };
      break;
    case "visual_text":
      {
        const label = data?.specifications?.label ?? "";
        conditionalProps.productPlacementImageUrl = localProductPlacementImageUrl;
        conditionalProps.productPlacementImagePPType = productPlacementImagePPType;
        conditionalProps.productPlacementImageDocId = productPlacementImageDocId;
        if (projectId) {
          conditionalProps.aspectRatio = aspectRatio;
          conditionalProps.text = label;
        }
        conditionalProps["visual_text"] = {
          aspectRatio,
          text: label,
        };
      }
      break;
    case "video_script":
      {
        const scriptDuration = String(getScriptLengthValue(data?.scriptLength?.label));
        conditionalProps["video_script"] = {
          script: scriptDuration,
          isImageIncluded: isImageIncluded,
        };
        if (isImageIncluded) {
          conditionalProps.productPlacementImageUrl = localProductPlacementImageUrl;
          conditionalProps.productPlacementImagePPTyp = productPlacementImagePPType;
          conditionalProps.productPlacementImageDocId = productPlacementImageDocId;
        }
        if (projectId) {
          conditionalProps.script = scriptDuration;
          conditionalProps.isImageIncluded = isImageIncluded;
        }
      }
      break;
  }

  return { ...baseObject, ...(projectId ? conditionalProps : { ...conditionalProps }) };
};

export const getRequestBody = ({
  data,
  assetType,
  IMAGE_DIMENSION_OPTIONS,
  projectId,
  localProductPlacementImageUrl,
  productPlacementImagePPType,
  productPlacementImageDocId,
  isImageIncluded,
  enableMergedPpfgFlow,
  referenceImage,
  raiChecks,
  inputImagePath,
}: RequestBodyParams) => {
  const aspectRatio = determineAspectRatio(data, localProductPlacementImageUrl, IMAGE_DIMENSION_OPTIONS, enableMergedPpfgFlow);
  return constructResponseObject({
    assetType,
    data,
    projectId,
    aspectRatio,
    localProductPlacementImageUrl,
    productPlacementImagePPType,
    productPlacementImageDocId,
    isImageIncluded,
    referenceImage,
    raiChecks,
    inputImagePath,
  });
};

export const getFieldsData = (config: FieldsDataConfig) => {
  const { label, classNames = "", wrapperClassName = "", radioGroupClassName = "", errorMessageId, useFieldset } = config;

  return [
    {
      type: "radio" as const,
      radioGroupClassName: `bg-white border-1 rounded-lg px-6 pb-6 pt-5 mb-2 ${radioGroupClassName}`,
      optionLabelClassName: "!normal-case",
      messagePosition: "top" as const,
      classNames,
      wrapperClassName,
      errorMessageId: errorMessageId,
      ...(useFieldset ? { useFieldset: true } : { ariaLabel: label }),
      ...config,
    },
  ];
};

export const getCombinedErrorMessage = (errors: FieldErrors) => {
  const messages: string[] = [];
  for (const key in errors) {
    const error = errors[key];

    if (!error) continue;

    if (typeof error.message === "string") {
      messages.push(error.message);
    } else if (typeof error === "object" && "message" in error) {
      const msg = (error as { message: unknown }).message;
      if (typeof msg === "string") {
        messages.push(msg);
      } else if (typeof msg === "object" && msg !== null) {
        messages.push(getCombinedErrorMessage(msg));
      }
    } else if (typeof error === "object") {
      messages.push(getCombinedErrorMessage(error as FieldErrors));
    }
  }
  return messages.join(" | ");
};

export const getImageType = (data: FormValues) => {
  const { assetType, productImageOption, videoScriptIncludeImages } = data;

  if (!assetType || assetType === AssetTypeField.TEXT || (assetType === AssetTypeField.VIDEO_SCRIPT && !videoScriptIncludeImages)) {
    return "";
  }

  if (productImageOption?.id === FULLY_GENERATED_IMAGE) {
    return "fully_generated";
  }
  if (productImageOption?.id === IMAGE_GENERATED_BACKGROUND) {
    return "product_placement_image";
  }
  return "";
};

export const getNumberOfGeneratedImage = (assetType: string, noOfImages: string) => {
  if ([AssetTypeField.VISUAL, AssetTypeField.VISUAL_AND_TEXT].includes(assetType as AssetTypeField)) {
    return noOfImages;
  }
  return "";
};

export const getGTMEventData = ({
  pageIndex = 1,
  stepName,
  errors,
  data,
  timeTaken,
  linkSection,
  pageName,
}: {
  pageIndex?: number;
  stepName: string;
  errors: FieldErrors;
  data: FormValues;
  timeTaken?: string;
  linkSection?: string;
  pageName?: string;
}) => {
  const errorMessage = getCombinedErrorMessage(errors);
  const currentPageName = pageName ?? steps?.[pageIndex - 1]?.label ?? "";

  const optionalObject = {
    ...(timeTaken ? { timeTaken } : {}),
    ...(errorMessage
      ? {
          errorMessage,
          errorType: GA_ERROR_TYPE.FORM,
          errorLocation: currentPageName,
        }
      : {}),
    ...(data?.copyOutput ? { copyOutput: data.copyOutput.label } : {}),
    ...(data?.productImageOption ? { imageType: getImageType(data) } : {}),
    ...(data?.imageDimensions ? { dimensionName: data.imageDimensions } : {}),
    ...(data?.scriptLength ? { videoScriptLength: data.scriptLength.label } : {}),
    ...(data?.assetType ? { assetType: data.assetType } : {}),
    ...(data?.channel ? { channelName: data.channel.label } : {}),
    ...(data?.specifications ? { specificationName: data.specifications.label } : {}),
    ...(data?.videoScriptIncludeImages ? { videoScriptImageIncluded: data.videoScriptIncludeImages } : {}),
    ...(data?.noOfImages ? { imageNumber: getNumberOfGeneratedImage(data.assetType, data.noOfImages) } : {}),
  };

  return {
    pageName: currentPageName,
    linkSection: linkSection ?? GTM_USERFLOW.ASSET_GENERATION_LINK,
    linkText: CONTINUE,
    stepName: stepName,
    errorMessage: errorMessage,
    ...optionalObject,
  };
};

export const getAssetTypeAspectRatio = (requestBody: ProjectOptionPostRequestBody): string => {
  switch (requestBody.assetType) {
    case ASSET_GENERATION_TYPES.VISUAL_TEXT:
      return requestBody[ASSET_GENERATION_TYPES.VISUAL_TEXT]?.aspectRatio as string;
    case ASSET_GENERATION_TYPES.VISUAL:
      return requestBody[ASSET_GENERATION_TYPES.VISUAL]?.aspectRatio as string;
    default:
      return DEFAULT_ASPECT_RATIO;
  }
};

export const getImageDimensionsToSave = (
  imageDimensionOptions: string,
  localProductPlacementImageUrl?: string,
  userSelectedDimensions: string = "",
): string => {
  const hasProductImage = localProductPlacementImageUrl;
  const defaultDimension = imageDimensionOptions ?? "";

  if (hasProductImage) return userSelectedDimensions || defaultDimension;

  return userSelectedDimensions || "";
};

export const getSpecificationsDefaultOption = (hasUniversalAccess: boolean) => {
  if (hasUniversalAccess) return PROJECT_OPTIONS_CONSTANT.SPECIFICATIONS_OPTIONS[1];
  return defaultRadioOption;
};
